/**
 * One bounded, cache-bypassing request for the player's probes.
 *
 * Every probe the player makes is a question about the network right now, so
 * the cache is bypassed, and every probe has a caller that stops retrying
 * while one is in flight, so an unbounded request would stop recovery for
 * good rather than just delay it. The result says how the request ended,
 * because the callers care: a rejected fetch is what a CORS-hidden response
 * looks like, while a timeout says nothing about the server at all.
 */

export type TimedFetchResult =
  | { response: Response }
  | { failure: 'timeout' | 'rejected' };

export interface TimedFetchOptions {
  method?: 'GET' | 'HEAD';
  /**
   * `no-cors` asks only whether the server answers: the response is opaque,
   * but a request nothing answers still rejects.
   */
  mode?: 'cors' | 'no-cors';
  timeoutMs: number;
  fetchImpl?: typeof fetch;
}

export async function fetchWithTimeout(
  url: string,
  { method = 'GET', mode = 'cors', timeoutMs, fetchImpl = fetch }:
    TimedFetchOptions,
): Promise<TimedFetchResult> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);

  try {
    // No custom headers and no credentials, so this stays a CORS-simple
    // request and never costs a preflight.
    const response = await fetchImpl(url, {
      method,
      mode,
      cache: 'no-store',
      signal: controller.signal,
    });
    return { response };
  } catch {
    return { failure: controller.signal.aborted ? 'timeout' : 'rejected' };
  } finally {
    clearTimeout(timer);
  }
}

/**
 * Release a response whose body the caller has no use for. `fetch` resolves
 * on headers; without this the browser keeps pulling the body in the
 * background until backpressure or garbage collection stops it.
 */
export function discardBody(response: Response): void {
  void response.body?.cancel().catch(() => {});
}
